import { ReactElement, ReactNode, RefObject, useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { loadablePlan } from './plan';
import { GridHandle } from '../Grid';
import { PlanMutationContext, useCreatePlanMutations } from './mutate';
import { LoadingContainer } from '@ff-it/ui';
import { useChannel, useChannelListener } from 'services';
import { ChannelEvent } from 'services/realtime/protocol';
import { BlockChannelEvent } from 'modules/campaign/block/events';
import { useRequestHandler } from 'modules/campaign/block/BlockProvider';
import { EntityContextType } from 'components';
import { BlockDetails } from 'modules/campaign/block/types';
export { usePlanMeta, useSmartOptions, usePlanSettings } from './plan';
export { gridAtom } from './grid';
export type { EntityKey, EntityRef, GridStore } from './grid';
export {
  collapsedGroupsAtom,
  maxDepthAtom,
  visibleKeysAtom,
  treeStructureAtom,
  useToggleGroup,
  useToggleAll,
} from './tree';
export type { TreeNodeMeta } from './tree';
export type { PlanStore } from './plan';
export { gridRowsAtom } from './rows';
export { usePlanMutations } from './mutate';
export { rowSummaryAtom, groupSummaryAtom, useGridSummaryRows } from './summary';
export { selectionEnabledAtom, useGridSelection, useToolbarSelection } from './selection';
export { usePlanStrategy } from './strategy';
export { usePlanPresence } from './presence';

type PlanProviderProps = {
  _revision: number;
  children: ReactNode;
  gridRef: RefObject<GridHandle>;
  mutate: EntityContextType<BlockDetails>['mutate'];
};

function LoadingBoundary({ children }: { children: ReactNode }) {
  const [value] = useAtom(loadablePlan);

  return (
    <LoadingContainer loading={value.state === 'loading'} className="flex-grow-1 flex-column d-flex">
      {value.state === 'hasData' && children}
    </LoadingContainer>
  );
}

export function PlanProvider({ _revision, children, gridRef, mutate }: PlanProviderProps): ReactElement {
  const requestHandler = useRequestHandler();
  const mutations = useCreatePlanMutations(requestHandler, gridRef, mutate);
  const channel = useChannel();
  useChannelListener(
    channel,
    useCallback(
      (message: ChannelEvent<BlockChannelEvent>) => {
        const expectedRevision = (requestHandler.revisionRef.current || 0) + 1;
        const revision = message.payload.revision;

        // we patch the plan if we have no gaps, esle just let block handler deal with this
        if (message.payload.event === 'PLAN/PATCH' && expectedRevision === revision) {
          console.log(`[PlanProvider]: event=${message.payload.event} revision=${revision}`);
          requestHandler.revisionRef.current = message.payload.revision;
          mutations.applyDiff(message.payload.data);
          return true;
        }
      },
      [requestHandler, mutations.applyDiff],
    ),
    // fires before block
    1,
  );

  const [error, setError] = useState<unknown>(undefined);

  // !WARNING: this assumes it gets remoundted and store is jotai store is flushed
  // biome-ignore lint/correctness/useExhaustiveDependencies: track _revision
  useEffect(() => {
    try {
      mutations.loadPlan();
    } catch (error) {
      setError(error);
    }
  }, [mutations.loadPlan, _revision]);

  if (error) {
    throw error;
  }

  return (
    <PlanMutationContext.Provider value={mutations}>
      <LoadingBoundary>{children}</LoadingBoundary>
    </PlanMutationContext.Provider>
  );
}
